import { useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow } from "date-fns";
import { trpc } from "../../lib/api/trpc";

type GapItem = {
  conversationId: string;
  query: string;
  confidence: string;
  source: string;
  askedAt: string;
  askedCount: number;
};

type GapResponse = {
  gaps: GapItem[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  summary: {
    totalGaps: number;
    insufficientCount: number;
    lowCount: number;
    topRepeatedGap: string | null;
  };
};

function SourceBadge({ source }: { source: string }) {
  const labels: Record<string, string> = {
    TEAMS_BOT: "Teams",
    ADMIN_CHAT: "Admin",
    API: "API",
  };
  return (
    <span className="text-[12.5px] text-[var(--text-3)]">
      {labels[source] || source}
    </span>
  );
}

export function GapAnalysisPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);

  const range = (searchParams.get("range") || "30") as "7" | "30" | "90";
  const confidenceFilter = (searchParams.get("confidence") || "both") as
    | "insufficient"
    | "low"
    | "both";
  const page = parseInt(searchParams.get("page") || "0", 10);

  // @ts-ignore - tRPC type inference through `as any` router cast
  const { data, isLoading, isError } = trpc.analytics.getQueryGaps.useQuery({
    range,
    confidenceFilter,
    page,
    pageSize: 20,
  });

  const result = data as GapResponse | undefined;

  const updateParam = useCallback(
    (key: string, value: string) => {
      const params = Object.fromEntries(searchParams.entries());
      params[key] = value;
      if (key !== "page") params.page = "0";
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const setPage = useCallback(
    (p: number) => {
      const params = Object.fromEntries(searchParams.entries());
      params.page = String(p);
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExport = async () => {
    if (!result) return;
    const lines = result.gaps.map(
      (g, i) =>
        `${i + 1}. ${g.query} (asked ${g.askedCount} time${g.askedCount !== 1 ? "s" : ""})`,
    );
    const text = `Questions your knowledge base cannot answer (last ${range} days):\n${lines.join("\n")}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="space-y-[18px] animate-pulse">
        <div className="grid grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] px-5 py-[18px]"
            >
              <div className="h-4 w-20 bg-[var(--surface-2)] rounded mb-3" />
              <div className="h-8 w-16 bg-[var(--surface-2)] rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-[var(--err-bg)] border border-[var(--err-bg)] text-[var(--err-text)] rounded-[12px] p-4">
        <p className="text-[13px]">Failed to load gap analysis data.</p>
      </div>
    );
  }

  if (!result || result.summary.totalGaps === 0) {
    return (
      <div className="space-y-[18px]">
        <div className="flex justify-end">
          <div className="inline-flex bg-[var(--surface-2)] border border-[var(--border)] rounded-[9px] p-[3px]">
            {(["7", "30", "90"] as const).map((r) =>
              range === r ? (
                <button
                  key={r}
                  onClick={() => updateParam("range", r)}
                  className="px-[13px] py-[5px] text-[12.5px] font-semibold text-[#F4F2E8] bg-[var(--accent)] rounded-md transition-colors"
                >
                  {r}d
                </button>
              ) : (
                <button
                  key={r}
                  onClick={() => updateParam("range", r)}
                  className="px-[13px] py-[5px] text-[12.5px] text-[var(--text-3)] rounded-md hover:text-[var(--text-2)] transition-colors"
                >
                  {r}d
                </button>
              ),
            )}
          </div>
        </div>
        <div className="text-center py-16">
          <p className="text-[13px] text-[var(--text-3)]">
            No knowledge gaps detected in the selected period. All queries were
            answered with sufficient confidence.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-[18px]">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-3">
          <select
            value={confidenceFilter}
            onChange={(e) => updateParam("confidence", e.target.value)}
            className="h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-2)]"
          >
            <option value="both">Both</option>
            <option value="insufficient">Unanswerable only</option>
            <option value="low">Low confidence only</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] font-medium text-[var(--text-2)] hover:bg-[var(--surface-2)] transition-colors"
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
            {copied ? "Copied!" : "Export gaps"}
          </button>
          <div className="inline-flex bg-[var(--surface-2)] border border-[var(--border)] rounded-[9px] p-[3px]">
            {(["7", "30", "90"] as const).map((r) =>
              range === r ? (
                <button
                  key={r}
                  onClick={() => updateParam("range", r)}
                  className="px-[13px] py-[5px] text-[12.5px] font-semibold text-[#F4F2E8] bg-[var(--accent)] rounded-md transition-colors"
                >
                  {r}d
                </button>
              ) : (
                <button
                  key={r}
                  onClick={() => updateParam("range", r)}
                  className="px-[13px] py-[5px] text-[12.5px] text-[var(--text-3)] rounded-md hover:text-[var(--text-2)] transition-colors"
                >
                  {r}d
                </button>
              ),
            )}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] px-5 py-[18px]">
          <div className="text-[13px] text-[var(--text-3)] mb-[10px]">
            Total Gaps
          </div>
          <div className="font-serif text-[36px] font-normal leading-none text-[var(--text)]">
            {result.summary.totalGaps}
          </div>
        </div>
        <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] px-5 py-[18px]">
          <div className="flex items-center gap-2 mb-[10px]">
            <span className="inline-block w-[7px] h-[7px] rounded-full bg-[var(--err-dot)]" />
            <span className="text-[13px] text-[var(--text-3)]">
              Unanswerable
            </span>
          </div>
          <div className="font-serif text-[36px] font-normal leading-none text-[var(--err-text)]">
            {result.summary.insufficientCount}
          </div>
        </div>
        <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] px-5 py-[18px]">
          <div className="flex items-center gap-2 mb-[10px]">
            <span className="inline-block w-[7px] h-[7px] rounded-full bg-[var(--warn-dot)]" />
            <span className="text-[13px] text-[var(--text-3)]">
              Low Confidence
            </span>
          </div>
          <div className="font-serif text-[36px] font-normal leading-none text-[var(--warn-text)]">
            {result.summary.lowCount}
          </div>
        </div>
      </div>

      {/* Top repeated gap callout */}
      {result.summary.topRepeatedGap && (
        <div className="flex items-center gap-3 bg-[var(--warn-bg)] border border-[var(--warn-border)] rounded-[12px] px-[17px] py-[13px] text-[13px] text-[var(--warn-text)] leading-relaxed">
          <ExclamationTriangleIcon
            className="h-5 w-5 flex-shrink-0"
            style={{ stroke: "var(--warn-text)" }}
          />
          <p>
            Most repeated unanswered question:{" "}
            <span className="font-medium">
              &ldquo;{result.summary.topRepeatedGap}&rdquo;
            </span>{" "}
            — consider uploading a document that covers this topic.
          </p>
        </div>
      )}

      {/* Gaps table */}
      <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
        <table className="min-w-full">
          <thead>
            <tr className="bg-[var(--surface-2)] border-b border-[var(--border-2)]">
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                Query
              </th>
              <th className="text-right text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                Asked
              </th>
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                Confidence
              </th>
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                Source
              </th>
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                Date
              </th>
            </tr>
          </thead>
          <tbody>
            {result.gaps.map((gap, i) => {
              // gaps are aggregated, so conversationId can repeat \u2014 pair it with
              // the index for a stable-unique React key and per-row expand state.
              const rowKey = `${gap.conversationId}-${i}`;
              const isExpanded = expandedRows.has(rowKey);
              const truncated =
                gap.query.length > 100
                  ? gap.query.slice(0, 100) + "\u2026"
                  : gap.query;
              return (
                <tr
                  key={rowKey}
                  onClick={() => toggleRow(rowKey)}
                  className="border-b border-[var(--border-2)] hover:bg-[var(--surface-2)] cursor-pointer transition-colors"
                >
                  <td className="px-5 py-3 text-[13px] text-[var(--text)] max-w-md">
                    {isExpanded ? gap.query : truncated}
                  </td>
                  <td className="px-5 py-3 text-[13px] text-right whitespace-nowrap">
                    {gap.askedCount > 1 && (
                      <span className="font-mono text-[11.5px] text-[var(--text-3)] bg-[var(--surface-2)] border border-[var(--border-2)] px-[7px] py-[2px] rounded-md">
                        &times;{gap.askedCount}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-[13px] whitespace-nowrap">
                    <span
                      className={`inline-flex items-center text-[11.5px] font-medium px-[9px] py-[3px] rounded-[7px] ${
                        gap.confidence === "insufficient"
                          ? "bg-[var(--err-bg)] text-[var(--err-text)]"
                          : "bg-[var(--warn-bg)] text-[var(--warn-text)]"
                      }`}
                    >
                      {gap.confidence === "insufficient"
                        ? "Unanswerable"
                        : "Low confidence"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[13px] whitespace-nowrap">
                    <SourceBadge source={gap.source} />
                  </td>
                  <td className="px-5 py-3 text-[13px] whitespace-nowrap text-[var(--text-3)]">
                    {formatDistanceToNow(new Date(gap.askedAt), {
                      addSuffix: true,
                    })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination */}
        {result.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--border-2)]">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page <= 0}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] font-medium text-[var(--text-2)] hover:bg-[var(--surface-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Previous
            </button>
            <span className="text-[13px] text-[var(--text-3)]">
              Page {page + 1} of {result.pagination.totalPages}
            </span>
            <button
              onClick={() =>
                setPage(Math.min(result.pagination.totalPages - 1, page + 1))
              }
              disabled={page >= result.pagination.totalPages - 1}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] font-medium text-[var(--text-2)] hover:bg-[var(--surface-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Call to action */}
      <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-[14px] p-4">
        <p className="text-[13px] text-[var(--text-3)]">
          These questions represent gaps in your knowledge base. Upload
          documents that cover these topics to improve answer quality.{" "}
          <Link
            to="/documents/upload"
            className="text-[var(--accent-strong)] font-medium hover:underline"
          >
            Upload documents
          </Link>
        </p>
      </div>
    </div>
  );
}
